import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GlobalComponentService } from '@techmore/global';
import { Response } from 'express';
import { throwError } from 'rxjs';
import { OAuthError } from './share/entity/OauthError';
import { LoggerService } from './share/service/logger.service';

const displayErrorMessage = (isBrowser: boolean, globalComponentService: GlobalComponentService, loggerService: LoggerService,
  errorMessage: string, showCopyTip = false, ...loggerOptionalParams: unknown[]) => {
  if (isBrowser) {
    globalComponentService.openErrorAlert(errorMessage, { showCopyTip });
  } else {
    loggerService.error(errorMessage, ...loggerOptionalParams);
  }
};

/** 預設的 HttpErrorResponse 訊息處理方式 */
export function defaultHttpResponseErrorHandler(error: HttpErrorResponse, reqUrl: string,
  translateService: TranslateService, words: Record<string, string>,
  isBrowser: boolean, globalComponentService: GlobalComponentService, loggerService: LoggerService,
) {
  try {
    const requestId = error.headers.get('X-Request-Id');
    const requestTime = error.headers.get('X-Request-Time');
    
    if (error.error instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        const result = JSON.parse(reader.result.toString());
        displayErrorMessage(isBrowser, globalComponentService, loggerService, `${result.message}，URL: ${reqUrl}\n${words["errorCode"]}: ${requestId}，${words["errorTime"]}: ${requestTime}`, true);
      };
      reader.readAsText(error.error);
    } else {
      const oauthError: OAuthError = Object.assign(new OAuthError(translateService), {...error.error, code: requestId, time: requestTime});
      displayErrorMessage(isBrowser, globalComponentService, loggerService, `${oauthError.getMessage()}\nURL: ${reqUrl}`, true);
    }
  } catch (e) {
    displayErrorMessage(isBrowser, globalComponentService, loggerService, `${words['undefinedError']}，URL: ${reqUrl}`, false, e);
  }
}

/** 登入後操作 api 若失敗的訊息處理方式 */
export function printErrorHandler(error: unknown, reqUrl: string,
  translateService: TranslateService, globalComponentService: GlobalComponentService,
  words: Record<string, string>, isBrowser: boolean, loggerService: LoggerService) {
  
  // 如果是下載或單筆或多筆模組的 API 回傳 500 錯誤，且subStatus為999，不再顯示錯誤訊息，直接throwError
  const isSuppressErrorDisplay = /cm\/(download|single|normal)/.test(reqUrl) && error['status'] === 500 && error['error']['subStatus'] === '999';
  if (isSuppressErrorDisplay) {
    return throwError(error);
  }

  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 404:
        displayErrorMessage(isBrowser, globalComponentService, loggerService, `${words['addressNotFound']}，URL: ${reqUrl}`);
        break;
      case 401:
        // 401 是權控丟的，交給 BackStageTemplateService、SsoAuthService 處理
        // 除了機器人驗證失敗的訊息
        switch (error.error.subStatus) {
          case '401.612':
          case '401.611':
          case '401.610':
            break;
          default:
            try {
              displayErrorMessage(isBrowser, globalComponentService, loggerService, error.error.message);
            } catch (e) {
              displayErrorMessage(isBrowser, globalComponentService, loggerService, `${words['undefinedError']}，URL: ${reqUrl}`, false, e);
            }
            break;
        }
        break;
      default:
        defaultHttpResponseErrorHandler(error, reqUrl, translateService, words, isBrowser, globalComponentService, loggerService);
        break;
    }
  }
  return throwError(error);
}

/** 當檔案上傳失敗事件拋出 */
export function onUploadFailedEvent(evt, globalComponentService: GlobalComponentService, isBrowser: boolean) {
  console.error(evt);

  if (isBrowser) {
    switch (evt.code) {
      case 'E1':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案格式錯誤');
        break;
      case 'E2':
        globalComponentService.openErrorAlert((evt.fileName || '') + '檔案大小錯誤');
        break;
      case 'E3':
        globalComponentService.openErrorAlert((evt.fileName || '') + '匯入失敗: ' + (evt.msg as Error).message);
        break;
    }
  }
}

/** 在 server side 和 client side 做路徑轉導 */
export function navigateCrossPlatform(isBrowser: boolean, router: Router, response: Response, path: string[]): Promise<unknown>|Response {
  if (isBrowser) {
    return router.navigate(path);
  }
  response.redirect(301, path.join('/'));
  response.end();
  return response;
}

/** 去除 url 中的 anchor 後綴 */
export function removeAnchorHref(url: string): string {
  const anchorIndex = url.indexOf('#');
  if (anchorIndex !== -1) {
    return url.substring(0, anchorIndex);
  }
  return url;
}